import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import {
  Box,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  useMediaQuery,
  Grid,
  Button,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toSentenceCase } from '../../utils/utils';
import { useTheme } from '@mui/material/styles';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useState, useEffect, useRef } from 'react';
import { LineChart } from '@mui/x-charts';
import LinearProgress from '@mui/material/LinearProgress';

const BenefitsSection = ({ dataField, propertyDetails }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isMobile) {
      const handleClickOutside = (event) => {
        if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
          setActiveTooltip(null);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMobile]);

  if (!dataField || !dataField.data || !dataField.output) {
    return <LinearProgress />;
  }

  const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip
      {...props}
      classes={{ popper: className }}
      placement='bottom'
      arrow
      open={isMobile ? activeTooltip === props.id : props.open}
      onClose={() => !isMobile && props.onClose?.()}
      enterTouchDelay={0}
      leaveTouchDelay={5000}
      interactive
    />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 11,
      padding: '10px',
      gutterBottom: '10px',
    },
  }));

  const TooltipButton = ({ title, id }) => (
    <div ref={tooltipRef}>
      <LightTooltip
        title={title}
        id={id}
        open={isMobile ? activeTooltip === id : undefined}
        onClose={() => !isMobile && setActiveTooltip(null)}
      >
        <IconButton
          onClick={() =>
            isMobile && setActiveTooltip(activeTooltip === id ? null : id)
          }
        >
          <InfoOutlinedIcon
            sx={{
              color: 'gray',
              fontSize: { xs: '16px', sm: '20px' },
            }}
          />
        </IconButton>
      </LightTooltip>
    </div>
  );

  const LastYearData = ({ dataField }) => {
    const [expanded, setExpanded] = useState(false);
    const visibleRows = expanded
      ? Object.values(dataField?.data?.noLoanLifetimeProjection?.year)
      : Object.values(dataField?.data?.noLoanLifetimeProjection?.year).slice(
          0,
          6,
        );

    const parseLakh = (val) => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const match = val.match(/^([\d.]+)\s*L$/i);
        if (match) {
          return parseFloat(match[1]) * 100000;
        }
        const num = Number(val.replace(/,/g, ''));
        return isNaN(num) ? 0 : num;
      }
      return 0;
    };

    // Calculate payable cost
    const netCost =
      (dataField?.output?.max_cost || 0) -
      (dataField?.output?.stateGovtSubsidy || 0) -
      (dataField?.output?.centralGovtSubsidy || 0);

    return (
      <Accordion
        defaultExpanded={false}
        sx={{
          maxWidth: '840px',
          borderRadius: '10px !important',
          border: '1px solid lightgray',
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
          '& .MuiAccordionSummary-content': {
            margin: { xs: '10px 0', sm: '12px 0' },
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            padding: { xs: '0 10px', sm: '0 16px' },
          }}
        >
          <Grid container spacing={{ xs: 3, sm: 2 }} alignItems='center'>
            <Grid item xs={12}>
              <Typography
                variant='h6'
                sx={{
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  marginBottom: { xs: '5px', sm: '8px' },
                }}
                fontWeight={'bold'}
              >
                Lifetime estimates
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant='body2'>Total savings</Typography>
              <Typography variant='h5' fontWeight='bold'>
                ₹
                {dataField?.output?.lifeTimeSavingsRounded?.toLocaleString(
                  'en-IN',
                )}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant='body2'>Approx cost</Typography>
              <Typography display={'flex'} variant='h5' fontWeight='bold'>
                ₹{netCost.toLocaleString('en-IN')}
                <TooltipButton
                  title={
                    <>
                      <Grid container>
                        <Grid item xs={12}>
                          <Typography
                            variant='body1'
                            fontWeight={'bold'}
                            component={'p'}
                            pb={'22px'}
                          >
                            Price details
                          </Typography>
                        </Grid>
                        <Grid item xs={8} pb={'9px'}>
                          <Typography
                            variant='subtitle2'
                            component={'p'}
                            sx={{
                              textAlign: 'left',
                            }}
                          >
                            Payable amount
                          </Typography>
                        </Grid>
                        <Grid item xs={4} pb={'9px'}>
                          <Typography
                            variant='subtitle2'
                            component={'p'}
                            sx={{
                              textAlign: 'right',
                            }}
                          >
                            ₹
                            {dataField?.output?.max_cost?.toLocaleString(
                              'en-IN',
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={8} pb={'9px'}>
                          <Typography
                            variant='subtitle2'
                            component={'p'}
                            sx={{
                              textAlign: 'left',
                            }}
                          >
                            State government subsidy
                          </Typography>
                        </Grid>
                        <Grid item xs={4} pb={'9px'}>
                          <Typography
                            variant='subtitle2'
                            component={'p'}
                            sx={{
                              color: theme.palette.primary.main,
                              textAlign: 'right',
                            }}
                          >
                            -₹
                            {Number(
                              dataField?.output?.stateGovtSubsidy,
                            )?.toLocaleString('en-IN')}
                          </Typography>
                        </Grid>
                        <Grid item xs={8} pb={'9px'}>
                          <Typography
                            variant='subtitle2'
                            component={'p'}
                            sx={{
                              textAlign: 'left',
                            }}
                          >
                            Central government subsidy
                          </Typography>
                        </Grid>
                        <Grid item xs={4} pb={'9px'}>
                          <Typography
                            variant='subtitle2'
                            component={'p'}
                            sx={{
                              color: theme.palette.primary.main,
                              textAlign: 'right',
                            }}
                          >
                            -₹
                            {Number(
                              dataField?.output?.centralGovtSubsidy,
                            )?.toLocaleString('en-IN')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} pt={'7px'} pb={'16px'}>
                          <Divider />
                        </Grid>
                        <Grid item xs={8} pb={'9px'}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 'bold',
                              textAlign: 'left',
                            }}
                          >
                            Net project cost
                          </Typography>
                        </Grid>
                        <Grid item xs={4} pb={'9px'}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 'bold',
                              textAlign: 'right',
                            }}
                          >
                            ₹
                            {(
                              dataField?.output?.max_cost -
                              dataField?.output?.stateGovtSubsidy -
                              dataField?.output?.centralGovtSubsidy
                            )?.toLocaleString('en-IN')}
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  }
                  id='project-cost'
                />
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant='body2'>Net benefit</Typography>
              <Typography variant='h5' fontWeight='bold'>
                ₹
                {(
                  dataField?.output?.lifeTimeSavingsRounded -
                  (dataField?.output?.max_cost -
                    (dataField?.output?.stateGovtSubsidy || 0) -
                    (dataField?.output?.centralGovtSubsidy || 0))
                ).toLocaleString('en-IN')}
              </Typography>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: isMobile ? '0px' : 'unset' }}>
          <Stack>
            <LineChart
              xAxis={[
                {
                  scaleType: 'band',
                  data:
                    dataField !== ''
                      ? Object.values(
                          dataField?.data?.noLoanLifetimeProjection?.year,
                        )
                      : [],
                },
              ]}
              series={[
                {
                  data:
                    dataField !== ''
                      ? Object.values(
                          dataField?.data?.noLoanLifetimeProjection
                            .cumulativeSavings,
                        )
                      : [],
                  label: 'Cumulative savings',
                  color: '#7CC674',
                  area: true,
                },
              ]}
              borderRadius={10}
              height={isMobile ? 200 : 300}
              margin={{
                left: isMobile ? 35 : 50,
                right: isMobile ? 35 : 50,
                top: isMobile ? 20 : 60,
                bottom: 30,
              }}
              leftAxis={null}
              slotProps={{
                legend: {
                  direction: isMobile ? 'row' : 'column',
                  position: {
                    vertical: 'top',
                    horizontal: 'right',
                  },
                  padding: isMobile ? 5 : 10,
                  itemMarkWidth: isMobile ? 10 : 13,
                  itemMarkHeight: isMobile ? 10 : 13,
                  labelStyle: {
                    fontSize: isMobile ? '11px' : '13px',
                  },
                },
              }}
            >
              {dataField !== '' && (
                <ChartsReferenceLine
                  y={parseLakh(netCost || ['0'])}
                  label='Net project cost'
                  labelStyle={{ fontSize: '12px' }}
                  labelAlign='start'
                />
              )}
            </LineChart>
            <TableContainer
              component={'div'}
              sx={{
                paddingInline: { sm: '16px', xs: '3px' },
              }}
            >
              <Table
                sx={{ minWidth: '100%' }}
                aria-label='simple table'
                size='small'
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { sm: '14px', xs: '13px' },
                      }}
                    >
                      Year
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { sm: '14px', xs: '13px' },
                      }}
                    >
                      Generation
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { sm: '14px', xs: '13px' },
                      }}
                    >
                      Savings
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { sm: '14px', xs: '13px' },
                      }}
                    >
                      Total savings
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataField !== '' &&
                    visibleRows.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell
                          component='th'
                          scope='row'
                          sx={{ fontSize: { sm: '14px', xs: '11.5px' } }}
                        >
                          {row}
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontSize: { sm: '14px', xs: '11.5px' } }}
                        >
                          {parseLakh(
                            dataField?.data?.noLoanLifetimeProjection
                              .units_produced_solar?.[`${index}`],
                          ).toLocaleString('en-IN')}{' '}
                          units
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontSize: { sm: '14px', xs: '11.5px' } }}
                        >
                          ₹{' '}
                          {parseLakh(
                            dataField?.data?.noLoanLifetimeProjection
                              ?.savings?.[`${index}`],
                          ).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontSize: { sm: '14px', xs: '11.5px' } }}
                        >
                          ₹{' '}
                          {parseLakh(
                            dataField?.data?.noLoanLifetimeProjection
                              .cumulativeSavings?.[`${index}`],
                          ).toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              {dataField !== '' &&
                Object.values(dataField?.data?.noLoanLifetimeProjection?.year)
                  .length > 6 && (
                  <Box display='flex' justifyContent='center' mt={2}>
                    <Button
                      onClick={() => setExpanded(!expanded)}
                      endIcon={
                        expanded ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )
                      }
                      size='small'
                    >
                      {expanded ? 'Show Less' : 'Read More'}
                    </Button>
                  </Box>
                )}
            </TableContainer>
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  const FirstYearData = ({ dataField }) => {
    const [expanded, setExpanded] = useState(false);
    const rows = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const visibleRows = expanded ? rows : rows.slice(0, 6);

    return (
      <Accordion
        defaultExpanded={false}
        sx={{
          maxWidth: '840px',
          borderRadius: '10px !important',
          border: '1px solid lightgray',
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
          '& .MuiAccordionSummary-content': {
            margin: { xs: '10px 0', sm: '12px 0' },
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            padding: { xs: '0 10px', sm: '0 16px' },
          }}
        >
          <Grid container spacing={{ xs: 3, sm: 2 }} alignItems='center'>
            <Grid item xs={12}>
              <Typography
                display={'flex'}
                variant='h6'
                alignItems={'center'}
                sx={{
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  marginBottom: { xs: '5px', sm: '8px' },
                }}
                fontWeight={'bold'}
                gutterBottom
              >
                Annual estimates
                <TooltipButton
                  title='Calculated using state tariffs for low tension (LT) electricity connections.'
                  id='annual-estimate'
                />
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant='body2'>Bills before</Typography>
              <Typography variant='h5' fontWeight='bold'>
                ₹{dataField?.data?.billsWithoutSolar?.toLocaleString('en-IN')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant='body2'>Bills after</Typography>
              <Typography variant='h5' fontWeight='bold'>
                ₹{dataField?.data?.billsWithSolar?.toLocaleString('en-IN')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant='body2'>Reduction</Typography>
              <Typography variant='h5' fontWeight='bold'>
                {dataField?.data?.savings}%
              </Typography>
            </Grid>
          </Grid>
        </AccordionSummary>

        <AccordionDetails sx={{ padding: isMobile ? '0px' : 'unset' }}>
          <Stack>
            <LineChart
              xAxis={[
                { scaleType: 'band', data: dataField !== '' ? rows : [] },
              ]}
              series={[
                {
                  data:
                    dataField !== ''
                      ? dataField?.data?.summaryTable.BillBefore
                      : [],
                  label: 'Bills before solar',
                  color: '#B8BBBE',
                },
                {
                  data:
                    dataField !== ''
                      ? dataField?.data?.summaryTable.BillAfter
                      : [],
                  color: '#7CC674',
                  label: 'Bills after solar',
                  area: true,
                },
              ]}
              borderRadius={10}
              height={isMobile ? 250 : 350}
              margin={{
                left: isMobile ? 35 : 50,
                right: isMobile ? 35 : 50,
                top: isMobile ? 50 : 60,
                bottom: 30,
              }}
              leftAxis={null}
              slotProps={{
                legend: {
                  direction: 'column',
                  position: {
                    vertical: 'top',
                    horizontal: 'right',
                  },
                  padding: isMobile ? 5 : 10,
                  itemMarkWidth: isMobile ? 10 : 13,
                  itemMarkHeight: isMobile ? 10 : 13,
                  labelStyle: {
                    fontSize: isMobile ? '11px' : '13px',
                  },
                },
              }}
            />
            <TableContainer
              sx={{
                paddingInline: { sm: '16px', xs: '3px' },
              }}
            >
              <Table
                sx={{ minWidth: '100%' }}
                aria-label='simple table'
                size='small'
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { sm: '14px', xs: '13px' },
                      }}
                    >
                      Month
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { sm: '14px', xs: '13px' },
                      }}
                    >
                      Before solar
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { sm: '14px', xs: '13px' },
                      }}
                    >
                      After solar
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { sm: '14px', xs: '13px' },
                      }}
                    >
                      Savings
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row, index) => (
                    <TableRow
                      key={row.index}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell
                        component='th'
                        scope='row'
                        sx={{ fontSize: { sm: '14px', xs: '11.5px' } }}
                      >
                        {row}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ fontSize: { sm: '14px', xs: '11.5px' } }}
                      >
                        ₹
                        {dataField !== '' &&
                          Math.round(
                            dataField?.data?.summaryTable.BillBefore[index],
                          ).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ fontSize: { sm: '14px', xs: '11.5px' } }}
                      >
                        ₹
                        {dataField !== '' &&
                          Math.round(
                            dataField?.data?.summaryTable.BillAfter[index],
                          ).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ fontSize: { sm: '14px', xs: '11.5px' } }}
                      >
                        ₹
                        {dataField !== '' &&
                          Math.round(
                            dataField?.data?.summaryTable.BillBefore[index] -
                              dataField?.data?.summaryTable.BillAfter[index],
                          ).toLocaleString('en-IN')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length > 6 && (
                <Box display='flex' justifyContent='center' mt={2}>
                  <Button
                    onClick={() => setExpanded(!expanded)}
                    endIcon={
                      expanded ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )
                    }
                    size='small'
                  >
                    {expanded ? 'Show Less' : 'Read More'}
                  </Button>
                </Box>
              )}
            </TableContainer>
          </Stack>{' '}
        </AccordionDetails>
      </Accordion>
    );
  };

  const SummaryChart = () => {
    return (
      <Paper
        elevation={0}
        sx={{ padding: { xs: '15px 10px', sm: '20px 0px' }, maxWidth: '840px' }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant='body2'>Annual generation</Typography>
            <Typography
              display={'flex'}
              variant={isMobile ? 'h4' : 'h5'}
              // sx={{ fontSize: { xs: '2rem', sm: '2.125rem' } }}
              fontWeight='bold'
            >
              {dataField !== '' &&
                dataField?.output?.solarProducedUnits?.toLocaleString(
                  'en-IN',
                )}{' '}
              units
              <TooltipButton
                title={
                  <>
                    Estimated using data from weather stations near you and
                    solar panel performance in your area across the year
                    compiled by NASA database.&nbsp;
                    <a
                      href='/solar-generation'
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        color: 'inherit',
                      }}
                    >
                      Get solar generation data for your location
                    </a>
                    .
                  </>
                }
                id='annual-generation'
              />
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant='body2'>Lifetime generation</Typography>
            <Typography
              variant={isMobile ? 'h4' : 'h5'}
              // sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              fontWeight='bold'
            >
              {dataField !== '' &&
                dataField?.output?.lifeTimeProductionRounded?.toLocaleString(
                  'en-IN',
                )}{' '}
              units
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant='body2'>Subsidy amount</Typography>
            <Typography
              display={'flex'}
              variant={isMobile ? 'h4' : 'h5'}
              // sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              fontWeight='bold'
            >
              ₹
              {dataField !== '' &&
                Number(dataField?.output?.subsidyAmount)?.toLocaleString(
                  'en-IN',
                )}
              <TooltipButton
                title={
                  <>
                    <Grid container>
                      <Grid item xs={8} pb={'9px'}>
                        <Typography
                          variant='subtitle2'
                          component={'p'}
                          sx={{
                            textAlign: 'left',
                          }}
                        >
                          State government subsidy
                        </Typography>
                      </Grid>
                      <Grid item xs={4} pb={'9px'}>
                        <Typography
                          variant='subtitle2'
                          component={'p'}
                          sx={{
                            color: theme.palette.primary.main,
                            textAlign: 'right',
                          }}
                        >
                          +₹
                          {Number(
                            dataField?.output?.stateGovtSubsidy,
                          )?.toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                      <Grid item xs={8} pb={'9px'}>
                        <Typography
                          variant='subtitle2'
                          component={'p'}
                          sx={{
                            textAlign: 'left',
                          }}
                        >
                          Central government subsidy
                        </Typography>
                      </Grid>
                      <Grid item xs={4} pb={'9px'}>
                        <Typography
                          variant='subtitle2'
                          component={'p'}
                          sx={{
                            color: theme.palette.primary.main,
                            textAlign: 'right',
                          }}
                        >
                          +₹
                          {Number(
                            dataField?.output?.centralGovtSubsidy,
                          )?.toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} pt={'7px'} pb={'16px'}>
                        <Divider />
                      </Grid>
                      <Grid item xs={8} pb={'9px'}>
                        <Typography
                          variant='subtitle2'
                          component={'p'}
                          sx={{
                            textAlign: 'left',
                          }}
                        >
                          Total subsidy
                        </Typography>
                      </Grid>
                      <Grid item xs={4} pb={'9px'}>
                        <Typography
                          variant='subtitle2'
                          component={'p'}
                          sx={{
                            textAlign: 'right',
                          }}
                        >
                          ₹
                          {Number(
                            dataField?.output?.subsidyAmount,
                          )?.toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                }
                id='subsidy-details'
              />
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Box mt={4}>
      <Box mb={2}>
        <SummaryChart dataField={dataField} />
      </Box>

      <Box mb={3}>
        <FirstYearData dataField={dataField} />
      </Box>

      <Box>
        <LastYearData dataField={dataField} />
      </Box>
      <Typography variant='subtitle2' color={'gray'} pt={4}>
        Based on
        {propertyDetails?.billMonth
          ? toSentenceCase(propertyDetails.billMonth)
          : ' January'}{' '}
        bill of Rs {propertyDetails?.billAmount || '2,000'} on{' '}
        {propertyDetails?.propertyType
          ? propertyDetails.propertyType.toLowerCase()
          : 'residential'}{' '}
        property in {propertyDetails?.address?.state || 'Delhi'}
      </Typography>
    </Box>
  );
};

export default BenefitsSection;
